<script lang="ts">
  import { previousStageForReview, stagesForDealPipeline } from '$lib/utils/stageReview';
  import QueryStatus from '$lib/components/ui/QueryStatus.svelte';
  import DealJobSummary from './DealJobSummary.svelte';
  import DealActivityPanel from './DealActivityPanel.svelte';
  import { dealJobStatus } from '$lib/utils/dealJob';
  import { storedColor } from '$lib/utils/colors';
  import { untrack } from 'svelte';
  import { scheduleUndo } from '$lib/state/actionDialog.svelte';
  import { route } from '$lib/state/route.svelte';
  import { formatMoney, formatCurrencyTotals } from '$lib/utils/money';
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Clock, Trash2, BadgePercent, Kanban, Plus } from 'lucide-svelte';
  import { dealsApi, companiesApi, peopleApi, reportsApi } from '$lib/api/crm';
  import { session } from '$lib/state/session.svelte';
  import { getErrorMessage } from '$lib/api/client';
  import SearchBar from '$lib/components/ui/SearchBar.svelte';
  import Pagination from '$lib/components/ui/Pagination.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import CustomFieldsForm from '$lib/components/ui/CustomFieldsForm.svelte';
  import PersonCombobox from '$lib/components/ui/PersonCombobox.svelte';
  import DealAttachments from '$lib/components/entities/DealAttachments.svelte';
  import FilterChipRow from '$lib/components/ui/FilterChipRow.svelte';
  import type { FilterChip } from '$lib/components/ui/FilterChipRow.svelte';
  import RecommendationCard from '$lib/components/ui/RecommendationCard.svelte';
  import type { Deal } from '$lib/types';
  import { bookingCustomer, bookingError, ordersEnabled, isWonStage, isLostStage } from '$lib/utils/orders';
  import OrderFeedbackPanel from './OrderFeedbackPanel.svelte';
  import TcsNumber from '../ui/TcsNumber.svelte';
  import { toast } from '$lib/state/toast.svelte';

  const queryClient = useQueryClient();
  const selectedId = $derived(route.current.tab === 'deals' ? route.current.id : undefined);
  const recordQuery = createQuery(() => ({
    queryKey: ['deal', selectedId],
    queryFn: () => dealsApi.get(selectedId!),
    enabled: selectedId !== undefined
  }));
  $effect(() => {
    const id = selectedId, row = recordQuery.data;
    untrack(() => {
      if (row && id === row.id && (!showForm || editingId !== id)) openEdit(row);
      else if (!id && editingId !== null) showForm = false;
    });
  });

  let search = $state('');
  let page = $state(1);
  let stage = $state('');
  let stageReview = $state(false);
  let jobFilter = $state('');
  const JOB_STATUS_OPTIONS = [
    { value: 'not_booked', label: 'Not booked' }, { value: 'booked', label: 'Booked' },
    { value: 'invoiced_dispatched', label: 'Invoiced & dispatched' }, { value: 'installed_feedback_pending', label: 'Installed, feedback pending' },
    { value: 'feedback_in', label: 'Feedback in' }, { value: 'day7_call', label: 'Day-7 call' }, { value: 'closed', label: 'Closed' }
  ];
  function jobStatusLabel(deal: Deal) { return JOB_STATUS_OPTIONS.find(option => option.value === deal.job_status)?.label || dealJobStatus(deal); }
  let showForm = $state(false);
  let editingId = $state<number | null>(null);
  let formError = $state('');
  let dragId = $state<number | null>(null);
  let showCommissions = $state(false);

  const verticalId = $derived(
    session.selectedVertical === 'all' ? undefined : session.selectedVertical
  );
  // The deal-stage list is admin-configured per vertical (Vertical.settings.deal_stages).
  // Board view only makes sense with exactly one vertical in scope.
  const configuredStages = $derived(
    (typeof verticalId === 'number'
      ? session.me?.verticals.find((v) => v.id === verticalId)?.settings?.deal_stages
      : undefined) ?? []
  );
  // Kanban always available (mockup default pipeline): a single selected
  // vertical with no admin-configured deal_stages still gets a board, using
  // this fallback list. Deals whose stage matches none of these land in a
  // trailing "Other" column (see stageColumns below) rather than vanishing.
  const DEFAULT_STAGES = ['Lead in', 'Site survey', 'Proposal', 'Negotiation', 'Won'];
  const usingFallbackStages = $derived(typeof verticalId === 'number' && configuredStages.length === 0);
  const stages = $derived(usingFallbackStages ? DEFAULT_STAGES : configuredStages);
  const boardAvailable = $derived(typeof verticalId === 'number' && stages.length > 0);

  // Drives the list view's empty state: "nothing matches" (offer a reset) vs
  // "nothing here yet" (offer a create).
  const listFiltersActive = $derived(stage !== '' || search.trim() !== '' || jobFilter !== '' || stageReview);
  function clearListFilters() {
    stage = '';
    search = '';
    jobFilter = '';
    stageReview = false;
  }

  let view = $state<'board' | 'list'>('board');
  $effect(() => {
    if (!boardAvailable && view === 'board') view = 'list';
  });

  function emptyForm() {
    const formVerticalId = verticalId ?? session.me?.verticals[0]?.id;
    return {
      vertical_id: formVerticalId,
      company_id: undefined as number | undefined,
      person_id: undefined as number | undefined,
      name: '',
      customer_name: '', customer_phone: '', customer_email: '', customer_city: '', installer_name: '', book_order: false,
      amount: 0,
      currency: 'INR',
      stage: stagesForDealPipeline(formVerticalId, session.me?.verticals, DEFAULT_STAGES)[0] ?? '',
      close_date: '',
      referrer_person_id: undefined as number | undefined,
      commission_pct: undefined as number | undefined,
      commission_amount: undefined as number | undefined,
      custom: {} as Record<string, unknown>
    };
  }
  let form = $state(emptyForm());
  const formStages = $derived(stagesForDealPipeline(form.vertical_id, session.me?.verticals, DEFAULT_STAGES));
  function changeFormPipeline(event: Event) {
    form.vertical_id = Number((event.currentTarget as HTMLSelectElement).value);
    // Only an explicit new-deal pipeline change resets the draft stage.
    // Existing/deep-linked records retain their saved stage, even if legacy.
    if (editingId === null) form.stage = stagesForDealPipeline(form.vertical_id, session.me?.verticals, DEFAULT_STAGES)[0] ?? '';
  }
  let orderNumber = $state('');
  const orderEnabled = $derived(ordersEnabled(session.me?.verticals.find(v => v.id === form.vertical_id)));
  const booking = $derived(orderEnabled && (form.book_order || isWonStage(form.stage, session.me?.verticals.find(v => v.id === form.vertical_id)?.settings?.won_stages)));
  // Resolve the selected record directly: it may not be on the picker page.
  // Cleanup and draft identity prevent stale requests from touching another form.
  const touchedCustomer = new WeakMap<object, Set<string>>();
  function touchCustomer(event: Event) {
    const field = (event.target as HTMLInputElement).id.replace('order-', '').replaceAll('-', '_');
    const touched = touchedCustomer.get(form) ?? new Set<string>();
    touched.add(field); touchedCustomer.set(form, touched);
  }
  $effect(() => {
    const draft = form, personId = form.person_id, pipeline = form.vertical_id;
    const active = showForm && !orderNumber;
    let cancelled = false;
    const filled: Partial<Record<'customer_name' | 'customer_phone' | 'customer_email' | 'customer_city', string>> = {};
    if (active && personId && pipeline) {
      void peopleApi.get(personId).then(person => {
        if (cancelled || form !== draft || form.person_id !== personId || form.vertical_id !== pipeline || orderNumber) return;
        const customer = bookingCustomer({ ...draft, order_number: orderNumber }, person);
        for (const key of ['customer_name', 'customer_phone', 'customer_email', 'customer_city'] as const) {
          if (!draft[key].trim() && !touchedCustomer.get(draft)?.has(key)) {
            draft[key] = customer[key]; filled[key] = customer[key];
          }
        }
      }).catch(() => { /* Backend validates unavailable/scoped contacts at save. */ });
    }
    return () => {
      cancelled = true;
      // A different linked person/pipeline must not inherit our autofill.
      if (!orderNumber) for (const key of Object.keys(filled) as (keyof typeof filled)[]) {
        if (draft[key] === filled[key] && !touchedCustomer.get(draft)?.has(key)) draft[key] = '';
      }
    };
  });
  const customFieldDefs = $derived(session.customFieldsFor('deals', form.vertical_id));

  const listQuery = createQuery(() => ({
    queryKey: ['deals', { search, page, stage, verticalId, jobFilter, stageReview }],
    queryFn: () =>
      dealsApi.list({
        q: search || undefined,
        page,
        per_page: 25,
        stage: stage || undefined,
        stage_review: stageReview ? 'true' : undefined,
        job_status: jobFilter || undefined,
        vertical_id: verticalId
      })
  }));

  // ponytail: the board needs every open deal at once to fill columns, not a
  // page at a time. 300 comfortably covers a single vertical's pipeline for
  // this app's scale; if a vertical ever exceeds that, this should switch to
  // per-column pagination instead of raising the cap further.
  const boardQuery = createQuery(() => ({
    queryKey: ['deals-board', { search, verticalId, jobFilter, stageReview }],
    queryFn: () => dealsApi.list({ q: search || undefined, per_page: 300, vertical_id: verticalId, job_status: jobFilter || undefined, stage_review: stageReview ? 'true' : undefined }),
    enabled: boardAvailable
  }));

  const companiesQuery = createQuery(() => ({
    queryKey: ['companies-picker', { verticalId }],
    queryFn: () => companiesApi.list({ per_page: 100, vertical_id: verticalId })
  }));
  const peopleQuery = createQuery(() => ({
    queryKey: ['people-picker', { verticalId }],
    queryFn: () => peopleApi.list({ per_page: 100, vertical_id: verticalId })
  }));

  // Picker pages are intentionally small. Resolve only missing references
  // from the displayed rows, using existing scoped record reads.
  const displayedDeals = $derived(view === 'board' ? boardQuery.data?.results ?? [] : listQuery.data?.results ?? []);
  const visibleListDeals = $derived(listQuery.data?.results ?? []);
  const visibleBoardDeals = $derived(boardQuery.data?.results ?? []);
  const missingCompanyIds = $derived([...new Set(displayedDeals.map(d => d.company_id).filter((id): id is number => !!id && !companiesQuery.data?.results.some(c => c.id === id)))].sort((a, b) => a - b));
  const missingPersonIds = $derived([...new Set(displayedDeals.map(d => d.person_id).filter((id): id is number => !!id && !peopleQuery.data?.results.some(p => p.id === id)))].sort((a, b) => a - b));
  const referencedCompanies = createQuery(() => ({
    queryKey: ['deal-company-names', missingCompanyIds, verticalId],
    enabled: !!companiesQuery.data && missingCompanyIds.length > 0,
    queryFn: async () => (await Promise.allSettled(missingCompanyIds.map(id => companiesApi.get(id)))).flatMap(r => r.status === 'fulfilled' ? [r.value] : [])
  }));
  const referencedPeople = createQuery(() => ({
    queryKey: ['deal-person-names', missingPersonIds, verticalId],
    enabled: !!peopleQuery.data && missingPersonIds.length > 0,
    queryFn: async () => (await Promise.allSettled(missingPersonIds.map(id => peopleApi.get(id)))).flatMap(r => r.status === 'fulfilled' ? [r.value] : [])
  }));

  // Fetched only once the modal is opened, not on every Deals page load.
  const commissionsQuery = createQuery(() => ({
    queryKey: ['commissions', { verticalId }],
    queryFn: () => reportsApi.commissions({ vertical_id: verticalId }),
    enabled: showCommissions
  }));

  $effect(() => {
    search;
    stage;
    stageReview;
    jobFilter;
    verticalId;
    page = 1;
  });

  const saveMutation = createMutation(() => ({
    mutationFn: (body: Record<string, unknown>) =>
      editingId ? dealsApi.update(editingId, body) : dealsApi.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['deals-board'] });
      queryClient.invalidateQueries({ queryKey: ['deal'] });
      closeForm();
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  const deleteMutation = createMutation(() => ({
    mutationFn: (id: number) => dealsApi.remove(id),
    onError: (err: unknown) => toast.error(getErrorMessage(err)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['deals-board'] });
    }
  }));

  const moveMutation = createMutation(() => ({
    mutationFn: async ({ id, stage: s }: { id: number; stage: string }) => {
      const row = await dealsApi.get(id);
      const vertical = session.me?.verticals.find(v => v.id === row.vertical_id);
      const bookOrder = ordersEnabled(vertical) && isWonStage(s, vertical?.settings?.won_stages);
      // Send intent only: the linked person can change after this read.
      // Booking must hydrate the current contact inside the server transaction.
      return dealsApi.update(id, { stage: s, book_order: bookOrder });
    },
    onError: (err: unknown) => { formError = getErrorMessage(err); toast.error(formError); },
    onSuccess: (saved) => {
      if (editingId === saved.id) {
        form.stage = saved.stage;
        if (saved.order_number) {
          orderNumber = saved.order_number;
          form.person_id = saved.person_id ?? undefined;
          form.customer_name = saved.customer_name || '';
          form.customer_phone = saved.customer_phone || '';
          form.customer_email = saved.customer_email || '';
          form.customer_city = saved.customer_city || '';
          form.book_order = false;
        }
      }
      queryClient.setQueryData(['deal', saved.id], saved);
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['deals-board'] });
      queryClient.invalidateQueries({ queryKey: ['deal'] });
    }
  }));

  function openCreate() {
    if (selectedId) route.push({ tab: 'deals' });
    orderNumber = '';
    editingId = null;
    form = emptyForm();
    formError = '';
    showForm = true;
  }

  function openEdit(row: Deal) {
    if (selectedId !== row.id) route.push({ tab: 'deals', id: row.id });
    orderNumber = row.order_number || '';
    editingId = row.id;
    form = {
      vertical_id: row.vertical_id,
      company_id: row.company_id ?? undefined,
      person_id: row.person_id ?? undefined,
      name: row.name,
      customer_name: row.customer_name || '', customer_phone: row.customer_phone || '', customer_email: row.customer_email || '', customer_city: row.customer_city || '', installer_name: row.installer_name || '', book_order: false,
      amount: row.amount,
      currency: row.currency,
      stage: row.stage,
      close_date: row.close_date ? row.close_date.slice(0, 10) : '',
      referrer_person_id: row.referrer_person_id ?? undefined,
      commission_pct: row.commission_pct ?? undefined,
      commission_amount: row.commission_amount ?? undefined,
      custom: { ...row.custom }
    };
    formError = '';
    showForm = true;
  }

  function closeForm() {
    showForm = false;
    if (selectedId) route.push({ tab: 'deals' });
  }

  function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!form.name.trim()) {
      formError = 'Name is required';
      return;
    }
    formError = '';
    if (booking) { formError = bookingError(form); if (formError) return; }
    saveMutation.mutate({ ...form, ...bookingCustomer({ ...form, order_number: orderNumber }), book_order: booking, close_date: form.close_date || null });
  }

  function remove(row: Deal) {
    scheduleUndo(`Delete “${row.name}”`, () => deleteMutation.mutate(row.id));
  }

  function removeCurrent() {
    if (editingId === null) return;
    const id = editingId;
    scheduleUndo(`Delete “${form.name}”`, () => deleteMutation.mutate(id));
    closeForm();
  }

  function companyName(id: number | null): string {
    if (!id) return '';
    return companiesQuery.data?.results.find((c) => c.id === id)?.name ?? referencedCompanies.data?.find(c => c.id === id)?.name ?? `Company #${id}`;
  }

  function contactName(id: number | null): string {
    if (!id) return '';
    const p = peopleQuery.data?.results.find((p) => p.id === id) ?? referencedPeople.data?.find(p => p.id === id);
    return p ? `${p.first_name} ${p.last_name}`.trim() || p.email || `Contact #${id}` : `Contact #${id}`;
  }

  function contactInitials(id: number | null): string {
    const name = contactName(id);
    if (!name) return '--';
    return name
      .split(/\s+/)
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  function ageLabel(createdAt: string): string {
    const days = Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 86400000));
    if (days === 0) return 'Today';
    if (days === 1) return '1d';
    return `${days}d`;
  }

  // Persisted per-vertical settings; unconfigured stages share a stable default.
  function stageDot(s: string): string {
    return storedColor(session.me?.verticals.find(v => v.id === verticalId)?.settings?.stage_colors?.[s]);
  }
  // 8-digit hex = the same stage color at ~12% alpha, so a stage pill's fill
  // always agrees with its dot without a second hand-maintained palette.
  const stageTint = (s: string) => `${stageDot(s)}1F`;

  // Stage filter chips carry live counts in list view. boardQuery is enabled
  // whenever a board is available (not only while the board is on screen), so
  // list mode gets these for free off the same cached response — no extra
  // request, and no N-queries-per-stage. `count` on the response is the
  // server-side total (accurate for "All"); the per-stage tallies come off the
  // fetched rows and therefore inherit boardQuery's 300-row cap.
  const stageChips = $derived.by(() => {
    const rows = boardQuery.data?.results ?? [];
    const ready = boardQuery.data !== undefined;
    const chips: FilterChip[] = [{ id: '', label: 'All', count: ready ? boardQuery.data?.count : undefined }];
    for (const s of stages) {
      chips.push({
        id: s,
        label: s,
        dot: stageDot(s),
        count: ready ? rows.filter((d) => d.stage === s).length : undefined
      });
    }
    return chips;
  });

  const stageColumns = $derived.by(() => {
    const cols = stages.map((s) => {
      const cards = visibleBoardDeals.filter((d) => d.stage === s);
      const sum = cards.reduce((acc, d) => acc + d.amount, 0);
      return { name: s, dot: stageDot(s), cards, sum };
    });
    // Deals whose stage matches none of the (possibly fallback) columns
    // above still need somewhere to render, rather than silently vanishing
    // from the board — bucket them into a trailing "Other" column, and only
    // show it at all when it's non-empty.
    const known = new Set(stages);
    const other = visibleBoardDeals.filter((d) => !known.has(d.stage));
    if (other.length > 0) {
      cols.push({ name: 'Other', dot: '#999D9E', cards: other, sum: other.reduce((acc, d) => acc + d.amount, 0) });
    }
    return cols;
  });

  // "Open" excludes configured won/lost stages (legacy aliases when absent).
  // If a vertical's stage list has neither, fall back to
  // treating just the last configured stage as closed, matching the
  // mockup's single-terminal-stage assumption.
  const namedClosedStage = (s: string) => isWonStage(s, session.me?.verticals.find(v => v.id === verticalId)?.settings?.won_stages) || isLostStage(s, session.me?.verticals.find(v => v.id === verticalId)?.settings?.lost_stages);
  const isClosedStage = (s: string) => {
    const named = stages.some(namedClosedStage);
    return named ? namedClosedStage(s) : s === stages[stages.length - 1];
  };
  const openCount = $derived((boardQuery.data?.results ?? []).filter((d) => !isClosedStage(d.stage)).length);

  // ---- mobile board: one stage in focus at a time ----
  // A 288px column strip is unusable on a 390px phone (you pan blind between
  // stages), so below `sm` the board collapses to a chip row + the single
  // selected column, full width. Desktop keeps the side-by-side board.
  let focusStagePick = $state<string | null>(null);
  const focusStage = $derived(
    focusStagePick && stageColumns.some((c) => c.name === focusStagePick)
      ? focusStagePick
      : (stageColumns[0]?.name ?? '')
  );
  const focusColumn = $derived(stageColumns.find((c) => c.name === focusStage));
  const focusChips = $derived<FilterChip[]>(
    stageColumns.map((c) => ({ id: c.name, label: c.name, dot: c.dot, count: c.cards.length }))
  );

  // ---- stale-deal nudge ----
  // `updated_at` moves on any edit, not only a stage change, so this is
  // "nothing has been touched here in a while", not a strict stage-age. The
  // card says exactly that rather than implying the stronger claim, and the
  // primary action is to open the deal (a human decision), not to advance it.
  const STALE_DAYS = 21;
  let dismissedNudges = $state<Set<number>>(new Set());
  const staleNudge = $derived.by(() => {
    if (!boardAvailable) return null;
    let worst: Deal | null = null;
    let worstDays = 0;
    for (const d of boardQuery.data?.results ?? []) {
      if (isClosedStage(d.stage) || dismissedNudges.has(d.id)) continue;
      const days = Math.floor((Date.now() - new Date(d.updated_at).getTime()) / 86400000);
      if (days >= STALE_DAYS && days > worstDays) {
        worst = d;
        worstDays = days;
      }
    }
    if (!worst) return null;
    const idx = stages.indexOf(worst.stage);
    const next = idx >= 0 && idx < stages.length - 1 ? stages[idx + 1] : null;
    return { deal: worst, days: worstDays, next };
  });
  function dismissNudge(id: number) {
    dismissedNudges = new Set(dismissedNudges).add(id);
  }

  function onDragStart(id: number) {
    dragId = id;
  }
  function onDragEnd() {
    dragId = null;
  }
  function onDrop(newStage: string) {
    if (dragId !== null) {
      moveMutation.mutate({ id: dragId, stage: newStage });
      dragId = null;
    }
  }

  // Consumes the Header's "+ New deal" / topbar-search-result signal (see
  // session.svelte.ts's PageSignal doc).
  $effect(() => {
    const signal = session.consumeSignal('deals');
    if (!signal) return;
    if (signal.mode === 'create') openCreate();
    else openEdit(signal.record as Deal);
  });
</script>

<div class="tym-page flex h-full min-h-0 flex-col">
  {#if recordQuery.isError}<p class="tym-error-state">{getErrorMessage(recordQuery.error)} <button type="button" class="btn-secondary" onclick={() => recordQuery.refetch()}>Retry</button></p>{/if}
  <div class="mb-4 flex flex-shrink-0 flex-wrap items-center gap-3">
    <div>
      {#if boardQuery.data}
        <div class="text-[13px] text-[#666]">
          <span class="font-heading text-[15px] font-bold text-navy">{openCount}</span> open deals &middot;
          <span class="font-heading text-[15px] font-bold text-navy"
            >{formatCurrencyTotals(boardQuery.data.results.filter(d => !isClosedStage(d.stage)))}</span
          > pipeline
        </div>
      {/if}
      <div class="mt-px text-[11.5px] text-dark-gray">
        {#if !boardAvailable}
          Select a single vertical to enable the board view
        {:else if usingFallbackStages}
          Default pipeline shown &mdash; set custom stages in Verticals
        {:else}
          Stages set by admin in Verticals
        {/if}
      </div>
    </div>
    <span class="flex-1"></span>
    <div class="flex gap-[3px] rounded-[9px] border border-toyama-cloudy-gray bg-toyama-light-carbon p-[3px]">
      <button
        type="button"
        class="rounded-[7px] px-3.5 py-1.5 text-[12.5px] font-semibold {view === 'board'
          ? 'bg-navy text-white'
          : 'text-[#666]'}"
        disabled={!boardAvailable}
        onclick={() => (view = 'board')}
      >
        Board
      </button>
      <button
        type="button"
        class="rounded-[7px] px-3.5 py-1.5 text-[12.5px] font-semibold {view === 'list'
          ? 'bg-navy text-white'
          : 'text-[#666]'}"
        onclick={() => (view = 'list')}
      >
        List
      </button>
    </div>
  </div>

  <div class="mb-1 flex flex-shrink-0 items-start gap-3">
    <div class="min-w-0 flex-1">
      <SearchBar bind:value={search} onCreate={openCreate} createLabel="New deal" placeholder="Search deal name or order number…" />
    </div>
    <!-- icon-only below sm so Search + New deal + Commissions don't fight over
         a 390px toolbar -->
    <button
      type="button"
      class="btn-secondary !h-11 shrink-0"
      onclick={() => (showCommissions = true)}
      aria-label="Commissions"
    >
      <BadgePercent class="h-4 w-4" /> <span class="hidden whitespace-nowrap sm:inline">Commissions</span>
    </button>
  </div>

  <div class="mb-3 shrink-0 space-y-1 text-sm">
    <label class="flex items-center gap-2"><input type="checkbox" bind:checked={stageReview} />Needs pipeline stage review</label>
    {#if stageReview}<p class="text-xs text-dark-gray">Legacy stages without clear evidence stay unchanged (including in Other). Review the record and choose the supported stage to clear the flag. Do not assume payment, installation or customer satisfaction from a legacy Won label.</p>{/if}
  </div>

  {#snippet dealCard(d: Deal)}
    <div
      class="kanban-card {dragId === d.id ? 'dragging' : ''}"
      role="button"
      draggable="true"
      ondragstart={() => onDragStart(d.id)}
      ondragend={onDragEnd}
      onclick={() => openEdit(d)}
      onkeydown={(e) => e.key === 'Enter' && openEdit(d)}
      tabindex="0"
    >
      <div class="mb-[3px] font-heading text-[13.5px] font-bold leading-tight text-navy">
        {companyName(d.company_id) || d.name}
      </div>
      <div class="mb-2.5 text-[11.5px] text-dark-gray">{d.name}</div>
      {#if previousStageForReview(d.custom)}<p class="mb-2 text-xs font-semibold text-amber-800">Stage review: {previousStageForReview(d.custom)}</p>{/if}
      {#if d.order_number}<TcsNumber value={d.order_number} />{/if}
      {#if d.public_code}<p class="my-1 text-sm font-bold text-navy">{d.public_code}</p>{/if}
      <p class="mb-2 text-xs text-dark-gray">{jobStatusLabel(d)}</p>
      <div class="flex items-center justify-between">
        <span class="font-heading text-[15px] font-bold text-toyama-teal">{formatMoney(d.amount, d.currency)}</span>
        <span class="inline-flex items-center gap-[5px] text-[11px] text-dark-gray">
          <Clock class="h-[13px] w-[13px]" />
          {ageLabel(d.created_at)}
        </span>
      </div>
      {#if d.person_id}
        <div class="mt-[11px] flex items-center gap-[7px] border-t border-[#F0F0F0] pt-2.5">
          <span
            class="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full font-heading text-[10px] font-bold"
            style="background:#E3F0F1; color:#226A73"
          >
            {contactInitials(d.person_id)}
          </span>
          <span class="text-[11.5px] text-[#666]">{contactName(d.person_id)}</span>
        </div>
      {/if}
    </div>
  {/snippet}

  <label class="mb-3 flex flex-wrap items-center gap-2 text-xs text-dark-gray">Job status
    <select class="input !w-auto" bind:value={jobFilter}><option value="">All job statuses</option>{#each JOB_STATUS_OPTIONS as option}<option value={option.value}>{option.label}</option>{/each}</select>
  </label>
  {#if view === 'board'}
    {#if boardQuery.isLoading}
      <QueryStatus loading label="pipeline" />
    {:else if boardQuery.isError}
      <QueryStatus error={boardQuery.error} label="pipeline" onRetry={() => boardQuery.refetch()} />
    {:else}
      {#if staleNudge}
        {@const n = staleNudge}
        <div class="mb-3 shrink-0">
          <RecommendationCard
            question={`${companyName(n.deal.company_id) || n.deal.name} hasn't been touched in ${n.days} days.`}
            detail={`${n.deal.name} · ${n.deal.stage} · ${formatMoney(n.deal.amount, n.deal.currency)}`}
            acceptLabel="Review deal"
            onAccept={() => openEdit(n.deal)}
            alternatives={[
              ...(n.next
                ? [
                    {
                      label: `Move to ${n.next}`,
                      onSelect: () => moveMutation.mutate({ id: n.deal.id, stage: n.next as string })
                    }
                  ]
                : []),
              { label: 'Not now', onSelect: () => dismissNudge(n.deal.id) }
            ]}
            strength={Math.min(1, n.days / 60)}
            strengthLabel="How stale"
          />
        </div>
      {/if}

      <!-- mobile: chip row picks the stage, one full-width column in focus -->
      <div class="flex min-h-0 flex-1 flex-col sm:hidden">
        <FilterChipRow
          chips={focusChips}
          activeId={focusStage}
          onSelect={(id) => (focusStagePick = id)}
          ariaLabel="Focus a pipeline stage"
          class="mb-2.5 shrink-0"
        />
        {#if focusColumn}
          <div class="kanban-col kanban-col-focus" role="list">
            <div class="flex items-center gap-2 px-3.5 pb-2.5 pt-3.5">
              <span class="h-2.5 w-2.5 shrink-0 rounded-full" style="background:{focusColumn.dot}"></span>
              <span class="font-heading text-[15px] font-bold capitalize text-navy">{focusColumn.name}</span>
              <span class="font-heading text-[12px] font-semibold text-dark-gray">{focusColumn.cards.length}</span>
              <span class="flex-1"></span>
              <span class="font-heading text-[13px] font-bold text-toyama-teal"
                >{focusColumn.cards.length ? formatCurrencyTotals(focusColumn.cards) : '—'}</span
              >
            </div>
            <div class="flex flex-1 flex-col gap-2.5 overflow-y-auto px-2.5 pb-20 pt-0.5">
              {#each focusColumn.cards as d (d.id)}
                {@render dealCard(d)}
              {:else}
                <div class="px-1 py-3 text-center text-[12px] text-dark-gray">No deals in this stage</div>
              {/each}
            </div>
          </div>
        {/if}
      </div>

      <!-- desktop board: all stages side by side, drag to move -->
      <div class="hidden min-h-0 flex-1 gap-3.5 overflow-x-auto pb-2 sm:flex [scroll-snap-type:x_proximity]">
        {#each stageColumns as col (col.name)}
          <div
            class="kanban-col"
            role="list"
            ondragover={(e) => e.preventDefault()}
            ondrop={() => onDrop(col.name)}
          >
            <div class="flex items-center gap-2 px-3.5 pb-2.5 pt-3.5">
              <span class="h-2 w-2 shrink-0 rounded-full" style="background:{col.dot}"></span>
              <span class="font-heading text-[13px] font-bold capitalize text-navy">{col.name}</span>
              <span class="font-heading text-[11px] font-semibold text-dark-gray">{col.cards.length}</span>
              <span class="flex-1"></span>
              <span class="font-heading text-[12px] font-semibold text-toyama-teal"
                >{col.cards.length ? formatCurrencyTotals(col.cards) : '—'}</span
              >
            </div>
            <div class="flex flex-1 flex-col gap-2.5 overflow-y-auto px-2.5 pb-3 pt-0.5">
              {#each col.cards as d (d.id)}
                {@render dealCard(d)}
              {:else}
                <div class="px-1 py-3 text-center text-[12px] text-dark-gray">No deals</div>
              {/each}
            </div>
          </div>
        {/each}
      </div>
    {/if}
  {:else}
    {#if stages.length > 0}
      <FilterChipRow
        chips={stageChips}
        activeId={stage}
        onSelect={(id) => (stage = id)}
        ariaLabel="Filter deals by stage"
        class="mb-4"
      />
    {/if}

    {#if listQuery.isLoading}
      <div class="tym-table-wrap shrink-0 py-1" aria-busy="true" aria-label="Loading deals">
        {#each Array(4) as _, i (i)}
          <div class="flex items-center gap-5 border-b border-[#F5F5F4] px-[18px] py-[15px] last:border-none">
            <div class="flex-1">
              <div class="tym-shimmer h-3.5" style="width:{54 - i * 5}%"></div>
              <div class="tym-shimmer mt-2 h-2.5" style="width:{28 + i * 4}%"></div>
            </div>
            <div class="tym-shimmer h-[22px] w-[92px] shrink-0 rounded-pill"></div>
            <div class="tym-shimmer h-2.5 w-[104px] shrink-0"></div>
            <div class="tym-shimmer h-2.5 w-[44px] shrink-0"></div>
          </div>
        {/each}
      </div>
    {:else if listQuery.isError}
      <QueryStatus error={listQuery.error} label="deals" onRetry={() => listQuery.refetch()} />
    {:else if listQuery.data && listQuery.data.results.length === 0}
      <div class="tym-card tym-empty-panel shrink-0">
        <span class="tym-empty-icon"><Kanban class="h-6 w-6" /></span>
        <div class="tym-empty-title">
          {listFiltersActive ? 'No deals match these filters' : 'No deals yet'}
        </div>
        <div class="tym-empty-hint">
          {listFiltersActive
            ? 'Nothing in this stage for the current search — clear the filters to see the whole pipeline.'
            : 'Create the first deal for this vertical and it will show up here and on the board.'}
        </div>
        {#if listFiltersActive}
          <button type="button" class="btn-secondary mt-[18px]" onclick={clearListFilters}>Clear filters</button>
        {:else}
          <button type="button" class="btn-primary mt-[18px]" onclick={openCreate}>New deal</button>
        {/if}
      </div>
    {:else if listQuery.data}
      <!-- ponytail: `shrink-0`, not the flex-item default flex-shrink:1 —
           the page shell is `h-full flex-col` (needed by Board view above,
           not by this List view), and .tym-table-wrap is `overflow: hidden`
           (app.css, for rounded corners), so without shrink-0 the browser
           squeezes this wrap to whatever space is left below the search bar
           and silently clips rows past the fold with no scrollbar. shrink-0
           keeps its natural (full-row-count) height, which overflows
           .tym-page and lets <main class="overflow-y-auto"> (Header.svelte)
           — the same container every other entity list page scrolls
           through — do the scrolling instead. Same fix as IssuesPage
           (commit 7062bd9). -->
      <!-- desktop/tablet table; below sm it gives way to the card list -->
      <div class="tym-table-wrap tym-shadow hidden shrink-0 sm:block">
        <div class="tym-table-scroll">
          <table class="tym-table tym-dense">
            <thead>
              <tr>
                <th>Deal</th>
                <th>Stage</th>
                <th>Job status</th>
                <th>Contact</th>
                <th class="num">Value</th>
                <th class="num">Age</th>
                <th class="num"></th>
              </tr>
            </thead>
            <tbody>
              {#each visibleListDeals as row (row.id)}
                <tr class="clickable" onclick={() => openEdit(row)}>
                  <td>
                    <div class="font-semibold text-navy">{companyName(row.company_id) || row.name}</div>
                    <div class="text-[11.5px] text-dark-gray">{row.name}</div>
                    {#if previousStageForReview(row.custom)}<p class="text-xs font-semibold text-amber-800">Stage review: {previousStageForReview(row.custom)}</p>{/if}
                    {#if row.order_number}<TcsNumber value={row.order_number} />{/if}
                    {#if row.public_code}<p class="font-bold">{row.public_code}</p>{/if}
                  </td>
                  <td>
                    <span class="tym-pill capitalize" style="background:{stageTint(row.stage)}">
                      <span class="h-1.5 w-1.5 shrink-0 rounded-full" style="background:{stageDot(row.stage)}"></span>
                      {row.stage}
                    </span>
                  </td>
                  <td class="muted">{jobStatusLabel(row)}</td>
                  <td class="muted">{contactName(row.person_id)}</td>
                  <td class="num font-heading font-bold text-toyama-teal">{formatMoney(row.amount, row.currency)}</td>
                  <td class="num muted whitespace-nowrap">{ageLabel(row.created_at)}</td>
                  <td class="num">
                    <button
                      type="button"
                      class="btn-danger !h-10 !w-10 !p-0"
                      onclick={(e) => {
                        e.stopPropagation();
                        remove(row);
                      }}
                      aria-label={`Delete ${row.name}`}
                    >
                      <Trash2 class="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>

      <!-- mobile card list — a 6-column table is unreadable at 390px -->
      <div class="flex shrink-0 flex-col gap-2.5 sm:hidden">
        {#each visibleListDeals as row (row.id)}
          <div role="button" tabindex="0" class="tym-list-card" onclick={() => openEdit(row)} onkeydown={e => { if (e.key === 'Enter') openEdit(row); }}>
            <div class="flex items-center gap-2">
              <span class="tym-pill capitalize" style="background:{stageTint(row.stage)}">
                <span class="h-1.5 w-1.5 shrink-0 rounded-full" style="background:{stageDot(row.stage)}"></span>
                {row.stage}
              </span>
              <span class="flex-1"></span>
              <span class="inline-flex items-center gap-[5px] text-[11px] text-dark-gray">
                <Clock class="h-[13px] w-[13px]" />
                {ageLabel(row.created_at)}
              </span>
            </div>
            <div class="mt-2 text-[15px] font-semibold leading-[1.35] text-navy">
              {companyName(row.company_id) || row.name}
            </div>
            <div class="mt-0.5 text-[12px] text-dark-gray">{row.name}</div>
            {#if row.order_number}<TcsNumber value={row.order_number} />{/if}
            {#if row.public_code}<p class="font-bold">{row.public_code}</p>{/if}<p class="text-xs text-dark-gray">{jobStatusLabel(row)}</p>
            <div class="mt-2 flex items-center justify-between gap-2">
              <span class="font-heading text-[15px] font-bold text-toyama-teal"
                >{formatMoney(row.amount, row.currency)}</span
              >
              {#if contactName(row.person_id)}
                <span class="truncate text-[11.5px] text-[#666]">{contactName(row.person_id)}</span>
              {/if}
            </div>
          </div>
        {/each}
      </div>

      <Pagination
        page={listQuery.data.page}
        perPage={listQuery.data.per_page}
        count={listQuery.data.count}
        onChange={(p) => (page = p)}
      />
    {/if}
  {/if}

  <!-- floating create affordance on mobile, matching IssuesPage -->
  <button type="button" class="tym-fab flex items-center sm:hidden" onclick={openCreate}>
    <Plus class="h-5 w-5" />
    New deal
  </button>
</div>

{#if showForm}
  <Modal title={editingId ? 'Deal workspace' : 'New deal'} onClose={closeForm} wide>
    <form id="deal-form" class="space-y-4" onsubmit={submit}>
      {#if previousStageForReview(form.custom)}<p class="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Review previous stage: {previousStageForReview(form.custom)}. This legacy label does not prove payment, installation or satisfaction. Confirm the facts, then select the correct stage; the server clears the review flag when the stage is resolved.</p>{/if}
      {#if recordQuery.data?.id === editingId}<DealJobSummary deal={recordQuery.data} />{/if}
      {#if orderNumber}<p class="font-bold">Order {orderNumber}</p>{/if}
      {#if orderEnabled || orderNumber}
        <fieldset class="space-y-3" oninput={touchCustomer}><legend class="label">Order customer</legend>
          <label class="label" for="order-customer-name">Customer name</label><input id="order-customer-name" class="input" bind:value={form.customer_name} required={booking} />
          {#if orderNumber}<p class="text-sm">Booked phone and email are read-only. Use Completion feedback below for office contact correction; collect job sign-off on the public completion page.</p>{/if}
          <label class="label" for="order-customer-phone">Customer phone (E.164, e.g. +919876543210)</label><input id="order-customer-phone" class="input" type="tel" bind:value={form.customer_phone} required={booking} readonly={!!orderNumber} />
          <label class="label" for="order-customer-email">Customer email (optional)</label><input id="order-customer-email" class="input" type="email" bind:value={form.customer_email} readonly={!!orderNumber} />
          <label class="label" for="order-customer-city">Customer city</label><input id="order-customer-city" class="input" bind:value={form.customer_city} />
          <label class="label" for="order-installer-name">Installer name (optional)</label><input id="order-installer-name" class="input" bind:value={form.installer_name} maxlength="200" placeholder="Actual installer, if known" />
          {#if !orderNumber}<label><input type="checkbox" bind:checked={form.book_order} /> Book order (also required when marking Won)</label>{/if}
        </fieldset>
      {/if}
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        {#if session.me && session.me.verticals.length > 1}
          <div class="md:col-span-2">
            <label class="label" for="vertical">Vertical</label>
            <select id="vertical" class="input" bind:value={form.vertical_id} onchange={changeFormPipeline}>
              {#each session.me.verticals as v (v.id)}
                <option value={v.id}>{v.name}</option>
              {/each}
            </select>
          </div>
        {/if}
        <div class="md:col-span-2">
          <label class="label" for="name">Name</label>
          <input id="name" class="input" bind:value={form.name} required />
        </div>
        <div>
          <label class="label" for="company">Company</label>
          <select id="company" class="input" bind:value={form.company_id}>
            <option value={undefined}>-- none --</option>
            {#each companiesQuery.data?.results ?? [] as c (c.id)}
              <option value={c.id}>{c.name}</option>
            {/each}
          </select>
        </div>
        <div>
          <label class="label" for="person">Person</label>
          <PersonCombobox
            id="person"
            bind:value={form.person_id}
            people={peopleQuery.data?.results ?? []}
            verticalId={form.vertical_id}
            companyId={form.company_id}
          />
        </div>
        <div>
          <label class="label" for="amount">Amount</label>
          <input id="amount" type="number" step="0.01" class="input" bind:value={form.amount} />
        </div>
        <div>
          <label class="label" for="currency">Currency</label>
          <input id="currency" class="input" bind:value={form.currency} />
        </div>
        <div>
          <label class="label" for="stage">Stage</label>
          {#if formStages.length}
            <select id="stage" class="input" bind:value={form.stage}>
              {#if form.stage && !formStages.includes(form.stage)}<option value={form.stage}>{form.stage} (legacy — review required)</option>{/if}
              {#each formStages as s (s)}
                <option value={s}>{s}</option>
              {/each}
            </select>
          {:else}
            <input id="stage" class="input" bind:value={form.stage} />
          {/if}
        </div>
        <div>
          <label class="label" for="close_date">Close date</label>
          <input id="close_date" type="date" class="input" bind:value={form.close_date} />
        </div>
      </div>

      <div class="border-t border-[#F0F0F0] pt-4">
        <div class="mb-3 text-[11px] font-semibold uppercase tracking-[.08em] text-dark-gray">Referral</div>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div class="md:col-span-1">
            <label class="label" for="referrer">Referrer</label>
            <PersonCombobox
              id="referrer"
              bind:value={form.referrer_person_id}
              people={peopleQuery.data?.results ?? []}
              verticalId={form.vertical_id}
              placeholder="Search or add a referrer…"
            />
          </div>
          <div>
            <label class="label" for="commission_pct">Commission %</label>
            <input
              id="commission_pct"
              type="number"
              step="0.01"
              class="input"
              bind:value={form.commission_pct}
              placeholder="e.g. 5"
            />
          </div>
          <div>
            <label class="label" for="commission_amount">Commission amount</label>
            <input
              id="commission_amount"
              type="number"
              step="0.01"
              class="input"
              bind:value={form.commission_amount}
              placeholder="e.g. 5000"
            />
          </div>
        </div>
        <p class="mt-1.5 text-[11.5px] text-dark-gray">
          Set a percentage or a fixed amount, not both — a fixed amount takes priority if both are set.
        </p>
      </div>

      <CustomFieldsForm defs={customFieldDefs} values={form.custom} />

      {#if editingId}
        {#if orderNumber}<OrderFeedbackPanel dealId={editingId} onContactSaved={(contact) => { form.customer_phone = contact.phone; form.customer_email = contact.email; }} />{/if}
        <DealAttachments dealId={editingId} />
      {/if}

      {#if formError}<p class="text-sm text-red-600">{formError}</p>{/if}
    </form>

    {#if recordQuery.data?.id === editingId}<DealActivityPanel deal={recordQuery.data} stages={formStages} stagePending={moveMutation.isPending} onStage={stage => { if (editingId) moveMutation.mutate({ id: editingId, stage }); }} />{/if}

    {#snippet footer()}
      <div class="flex items-center justify-between gap-2">
        {#if editingId}
          <button type="button" class="btn-danger" onclick={removeCurrent}>Delete</button>
        {:else}
          <span></span>
        {/if}
        <div class="flex gap-2">
          <button type="button" class="btn-secondary" onclick={closeForm}>Cancel</button>
          <!-- sibling of the <form>, so it submits via the `form` attribute -->
          <button type="submit" form="deal-form" class="btn-primary" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    {/snippet}
  </Modal>
{/if}

{#if showCommissions}
  <Modal title="Commissions" onClose={() => (showCommissions = false)}>
    {#if commissionsQuery.isLoading}
      <p class="tym-loading-state">Loading commissions&hellip;</p>
    {:else if commissionsQuery.isError}
      <p class="tym-error-state">{getErrorMessage(commissionsQuery.error)}</p>
    {:else}
      <!-- the table's 640px min-width has to pan inside the sheet on a phone,
           not get clipped by the wrap's overflow:hidden -->
      <div class="tym-table-wrap tym-table-scroll tym-shadow">
        <table class="tym-table">
          <thead>
            <tr>
              <th>Referrer</th>
              <th class="num">Deals</th>
              <th class="num">Total commission</th>
            </tr>
          </thead>
          <tbody>
            {#each commissionsQuery.data?.results ?? [] as row (row.person_id)}
              <tr>
                <td class="font-semibold text-navy">{row.person_name || `#${row.person_id}`}</td>
                <td class="num muted">{row.deal_count}</td>
                <td class="num font-heading font-bold text-toyama-teal">
                  {#if 'currency' in row && typeof row.currency === 'string' && row.currency.trim()}
                    {formatMoney(row.total_commission, row.currency)}
                  {:else}
                    <span class="text-xs font-normal text-dark-gray">Currency breakdown unavailable</span>
                  {/if}
                </td>
              </tr>
            {:else}
              <tr><td class="empty" colspan="3">No referred deals with a commission yet.</td></tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </Modal>
{/if}
